import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { chatApi, type DebugSession } from "@/api/chat";
import { AlertTriangle, MessageSquare, Bot, Trash2 } from "lucide-react";

/**
 * Instance-admin only diagnostic page that lists EVERY chat session for an
 * agent — including:
 *   - sessions whose `channelId` no longer matches an existing channel
 *     (orphans — happens if a channel was deleted but its sessions weren't
 *     reassigned).
 *   - sessions with zero messages (empty shells, often left over from old
 *     bugs that double-fired the new-chat creation).
 *
 * Use it to find conversations that aren't appearing in the sidebar's
 * Recents under any channel, so we can recover or reassign them.
 *
 * URL: /tenants/:tenantId/debug/sessions
 */
export function DebugSessions() {
  const { tenantId, isInstanceAdmin } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const [agentId, setAgentId] = useState<string | null>(null);

  // Default to first agent
  const activeAgentId = agentId ?? agents[0]?.id ?? null;
  const activeAgent = agents.find((a) => a.id === activeAgentId);

  const { data: sessions = [], isLoading, error } = useQuery({
    queryKey: ["debug-sessions", activeAgentId],
    queryFn: () => chatApi.debugSessions(activeAgentId!),
    enabled: !!activeAgentId && isInstanceAdmin,
  });

  // Bulk delete every empty session for the active agent.
  const queryClient = useQueryClient();
  const [confirmingDeleteEmpty, setConfirmingDeleteEmpty] = useState(false);
  const [lastDeleteCount, setLastDeleteCount] = useState<number | null>(null);
  const deleteEmpty = useMutation({
    mutationFn: () => chatApi.debugDeleteEmptySessions(activeAgentId!),
    onSuccess: (result) => {
      setLastDeleteCount(result.deleted);
      queryClient.invalidateQueries({ queryKey: ["debug-sessions", activeAgentId] });
      // Also refresh the sidebar's recents list.
      queryClient.invalidateQueries({ queryKey: ["chat-sessions", activeAgentId] });
    },
  });

  // Group by channel display label (or "ORPHAN" / "GENERAL")
  const groups = useMemo(() => {
    const buckets = new Map<string, { label: string; isOrphan: boolean; rows: DebugSession[] }>();
    for (const s of sessions) {
      let key: string;
      let label: string;
      let isOrphan = false;
      if (s.channelId === null) {
        key = "__general__";
        label = "General (no channel)";
      } else if (s.channelName === null) {
        key = `__orphan__${s.channelId}`;
        label = `ORPHAN — channel ${s.channelId} no longer exists`;
        isOrphan = true;
      } else {
        key = s.channelId;
        label = s.channelName;
      }
      const bucket = buckets.get(key) ?? { label, isOrphan, rows: [] };
      bucket.rows.push(s);
      buckets.set(key, bucket);
    }
    // Orphans first so they're impossible to miss, then by row count desc
    return Array.from(buckets.values()).sort((a, b) => {
      if (a.isOrphan !== b.isOrphan) return a.isOrphan ? -1 : 1;
      return b.rows.length - a.rows.length;
    });
  }, [sessions]);

  const totals = useMemo(() => {
    const total = sessions.length;
    const empty = sessions.filter((s) => s.messageCount === 0).length;
    const orphan = sessions.filter((s) => s.channelId !== null && s.channelName === null).length;
    return { total, empty, orphan, real: total - empty };
  }, [sessions]);

  if (!isInstanceAdmin) {
    return (
      <CenterPanel>
        <Card>
          <CardContent className="p-8 text-center">
            <AlertTriangle size={32} className="mx-auto mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">
              This diagnostic page is restricted to instance admins.
            </p>
          </CardContent>
        </Card>
      </CenterPanel>
    );
  }

  return (
    <CenterPanel>
      <div className="mx-auto w-full max-w-5xl space-y-4 p-6">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">Chat sessions diagnostic</h1>
          <p className="text-sm text-text-muted mt-1">
            Lists every chat session for the selected agent, including orphan and empty
            sessions hidden from the regular sidebar.
          </p>
        </div>

        {/* Agent picker */}
        <Card>
          <CardContent className="p-4">
            <label className="text-xs font-medium text-text-secondary block mb-2">Agent</label>
            <div className="flex flex-wrap gap-2">
              {agents.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setAgentId(a.id)}
                  className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs transition-colors ${
                    a.id === activeAgentId
                      ? "border-accent bg-accent/10 text-accent"
                      : "border-border text-text-secondary hover:bg-surface-hover"
                  }`}
                >
                  <Bot size={12} />
                  {a.name}
                </button>
              ))}
              {agents.length === 0 && (
                <p className="text-sm text-text-muted">No agents in this tenant.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        {activeAgent && (
          <div className="grid grid-cols-4 gap-3">
            <SummaryCard label="Total sessions" value={totals.total} />
            <SummaryCard label="With messages" value={totals.real} accent="success" />
            <SummaryCard label="Empty (0 msgs)" value={totals.empty} accent="warn" />
            <SummaryCard label="Orphaned channel" value={totals.orphan} accent="danger" />
          </div>
        )}

        {/* Cleanup actions */}
        {activeAgent && totals.empty > 0 && (
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <Trash2 size={16} className="text-amber-600" />
              <p className="flex-1 text-sm text-text-secondary">
                Found <strong>{totals.empty}</strong> empty session{totals.empty === 1 ? "" : "s"}{" "}
                (zero messages) for {activeAgent.name}. These are usually leftovers from
                old new-chat double-fire bugs.
              </p>
              <Button
                size="sm"
                variant="destructive"
                disabled={deleteEmpty.isPending}
                onClick={() => setConfirmingDeleteEmpty(true)}
              >
                {deleteEmpty.isPending ? "Deleting…" : `Delete ${totals.empty} empty`}
              </Button>
            </CardContent>
          </Card>
        )}
        {lastDeleteCount !== null && lastDeleteCount > 0 && (
          <Card>
            <CardContent className="p-3 text-sm text-green-600">
              Deleted {lastDeleteCount} empty session{lastDeleteCount === 1 ? "" : "s"}.
            </CardContent>
          </Card>
        )}

        {/* Loading / error */}
        {isLoading && <p className="text-sm text-text-muted">Loading sessions…</p>}
        {error && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-red-500">Failed to load: {(error as Error).message}</p>
            </CardContent>
          </Card>
        )}

        {/* Groups */}
        {groups.map((group) => (
          <Card key={group.label}>
            <CardContent className="p-0">
              <div
                className={`flex items-center gap-2 border-b border-border px-4 py-2.5 text-sm font-medium ${
                  group.isOrphan ? "bg-red-500/10 text-red-500" : "text-text-primary"
                }`}
              >
                {group.isOrphan && <AlertTriangle size={14} />}
                {group.label}
                <Badge variant="secondary" className="ml-auto">
                  {group.rows.length}
                </Badge>
              </div>
              <div className="divide-y divide-border">
                {group.rows.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 px-4 py-2.5">
                    <MessageSquare
                      size={14}
                      className={s.messageCount === 0 ? "text-text-muted" : "text-accent"}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-text-primary">{s.title}</p>
                      <p className="text-[11px] text-text-muted">
                        {s.userEmail || (s.userId ? s.userId.slice(0, 8) : "system")} ·{" "}
                        created {formatDate(s.createdAt)} · updated {formatDate(s.updatedAt)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge variant={s.messageCount === 0 ? "secondary" : "default"}>
                        {s.messageCount} msg{s.messageCount === 1 ? "" : "s"}
                      </Badge>
                      <code className="text-[10px] text-text-muted">{s.id.slice(0, 8)}</code>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        {!isLoading && !error && sessions.length === 0 && activeAgent && (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-sm text-text-muted">No sessions exist for {activeAgent.name}.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirm bulk-delete modal */}
      {confirmingDeleteEmpty && activeAgent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setConfirmingDeleteEmpty(false)}
        >
          <div
            className="w-[min(400px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-sm font-semibold text-text-primary">
              Delete {totals.empty} empty session{totals.empty === 1 ? "" : "s"}?
            </h3>
            <p className="mb-4 text-xs text-text-secondary">
              This will permanently delete every chat session for{" "}
              <span className="font-medium text-text-primary">{activeAgent.name}</span>{" "}
              that has zero messages. Sessions with any conversation history will be left alone.
              This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmingDeleteEmpty(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={deleteEmpty.isPending}
                onClick={() => {
                  setLastDeleteCount(null);
                  deleteEmpty.mutate();
                  setConfirmingDeleteEmpty(false);
                }}
              >
                {deleteEmpty.isPending ? "Deleting…" : `Delete ${totals.empty} empty`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </CenterPanel>
  );
}

function SummaryCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: "success" | "warn" | "danger";
}) {
  const accentClass =
    accent === "success"
      ? "text-green-600"
      : accent === "warn"
        ? "text-amber-600"
        : accent === "danger"
          ? "text-red-500"
          : "text-text-primary";
  return (
    <Card>
      <CardContent className="p-3">
        <p className="text-[11px] uppercase tracking-wide text-text-muted">{label}</p>
        <p className={`mt-1 text-2xl font-semibold ${accentClass}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
